import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Row, Col, Input, Tag, Button, Checkbox, Empty, Modal, Descriptions, Space } from 'antd';
import { toast } from '../../../utils/toast.js';
import {
  SearchOutlined,
  InboxOutlined,
  DollarCircleOutlined,
  SmileOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  StarOutlined,
  DownloadOutlined,
  FileWordOutlined,
  InfoCircleOutlined,
  EditOutlined,
  FilterOutlined,
  CloseCircleOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { PHARMACY_FLOWS, isSopHiddenByConfig } from '../data/pharmacyToolData.js';
import { pharmacyToolApi } from '../../../api/pharmacy-tool.api.js';
import { useAuth } from '../../../contexts/AuthContext.jsx';

const formatVnd = (price) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);

export function PharmacyFlowMap({
  sops = [],
  config = {},
  onNavigateToForm,
  completedSopCodes = [],
  onToggleCompleteSop,
  // Quyền dùng công cụ SOP (GET /pharmacy-tool/access): chưa mua thì chỉ tải được 3 SOP dùng thử (freeCodes)
  sopAccess = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const buyPlan = sopAccess?.requiredPlans?.find((p) => p.price > 0) || null;
  const isLocked = (sop) =>
    !!sopAccess && !sopAccess.hasAccess && !(sopAccess.freeCodes || []).includes(String(sop?.code).toUpperCase());
  const isTrial = (sop) => !!sopAccess && !sopAccess.hasAccess && !isLocked(sop);
  const goToBuy = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location } });
      return;
    }
    if (buyPlan) {
      navigate(`/checkout/${buyPlan.id}?returnTo=${encodeURIComponent(`${location.pathname}${location.search}`)}`);
    } else {
      toast.info({ content: 'Liên hệ Zalo tư vấn viên để mua bộ SOP đầy đủ.', duration: 3 });
    }
  };
  const buyLabel = buyPlan ? `Mua bộ 26 SOP · ${formatVnd(buyPlan.price)}` : 'Mua bộ 26 SOP';
  const [selectedFlow, setSelectedFlow] = useState('all');
  const [searchKeyword, setSearchKeyword] = useState('');
  const showHiddenByConfig = false;
  const [previewSop, setPreviewSop] = useState(null);

  // Xác định cơ sở có hệ thống nội bộ không (dựa trên câu 3 & câu 4)
  const hasMultipleStaff = ['3-5', '6-10', '>10'].includes(config.staffCount);
  const isSoloOrNoInternal =
    config.hasInternalSystem === false ||
    config.hasInternalSystem === 'no' ||
    config.staffCount === '1' ||
    (!hasMultipleStaff && config.ownerPresence === 'chu-tu-ban');

  // Quy tắc ẩn hiện: 100% Matching trực tiếp với kết quả chẩn đoán 6 câu hỏi
  const isHiddenByConfig = (code) => {
    return isSopHiddenByConfig(code, config);
  };

  const getFlowObj = (sop) => {
    // 1. Ưu tiên tra cứu theo mã QT chuẩn trong qtCodes (tuyệt đối chính xác)
    const byCode = PHARMACY_FLOWS.find((f) => f.qtCodes.includes(sop.code));
    if (byCode) return byCode;

    // 2. Tra cứu theo flowId hoặc alias
    const flowKey = (sop.flow || '').toLowerCase();
    return (
      PHARMACY_FLOWS.find(
        (f) =>
          f.id === sop.flowId ||
          f.id.replace('flow-', '') === flowKey ||
          (flowKey === 'su_co' && f.id === 'flow-tinhhuong')
      ) || {
        name: sop.flowName || sop.flow || 'Quy trình',
        color: '#005537',
      }
    );
  };

  const getFlowIcon = (flowId) => {
    switch (flowId) {
      case 'flow-hang':
        return <InboxOutlined />;
      case 'flow-tien':
        return <DollarCircleOutlined />;
      case 'flow-khach':
        return <SmileOutlined />;
      case 'flow-nguoi':
        return <TeamOutlined />;
      case 'flow-tinhhuong':
        return <ThunderboltOutlined />;
      default:
        return <InboxOutlined />;
    }
  };

  // Filter SOPs
  const filteredSops = sops.filter((sop) => {
    // 1. Flow filter (sử dụng qtCodes để loại bỏ hoàn toàn lỗi lệch tên flow giữa DB và FE)
    if (selectedFlow !== 'all') {
      const targetFlow = PHARMACY_FLOWS.find((f) => f.id === selectedFlow);
      if (targetFlow) {
        if (!targetFlow.qtCodes.includes(sop.code)) return false;
      } else {
        const flowId = sop.flowId || `flow-${(sop.flow || '').toLowerCase()}`;
        if (flowId !== selectedFlow && !flowId.includes(selectedFlow.replace('flow-', ''))) return false;
      }
    }

    // 2. Keyword search
    if (searchKeyword.trim() !== '') {
      const kw = searchKeyword.toLowerCase().trim();
      const matchCode = sop.code?.toLowerCase().includes(kw);
      const matchTitle = sop.title?.toLowerCase().includes(kw);
      const matchKey = (sop.keyAdvice || sop.keyDetail || '').toLowerCase().includes(kw);
      const matchPurpose = (sop.purpose || sop.summary || '').toLowerCase().includes(kw);
      const matchExecutor = (sop.executor || sop.targetRole || '').toLowerCase().includes(kw);
      if (!matchCode && !matchTitle && !matchKey && !matchPurpose && !matchExecutor) return false;
    }

    // 3. Hidden by config: mặc định chỉ hiển thị quy trình phù hợp
    if (!showHiddenByConfig && isHiddenByConfig(sop.code)) return false;

    return true;
  });

  // Calculate dynamic counts
  const activeSopsInTotal = sops.filter((s) => !isHiddenByConfig(s.code));
  const totalActiveCount = activeSopsInTotal.length;

  const handleResetFilters = () => {
    setSelectedFlow('all');
    setSearchKeyword('');
  };

  const hasActiveFilters =
    selectedFlow !== 'all' ||
    searchKeyword.trim() !== '';

  const [downloadingCode, setDownloadingCode] = useState(null);

  const handleDownload = async (e, sop) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!sop || !sop.code) return;
    if (isLocked(sop)) {
      goToBuy();
      return;
    }

    try {
      setDownloadingCode(sop.code);
      toast.loading({
        content: `Đang tải tài liệu quy trình ${sop.code}...`,
        key: `dl-${sop.code}`,
        duration: 1.5,
      });
      await pharmacyToolApi.downloadSopFile(sop.code, sop.fileName);
      toast.success({
        content: `Đã tải về thành công: ${sop.fileName || `${sop.code}.docx`}`,
        key: `dl-${sop.code}`,
        duration: 2.5,
      });
    } catch (error) {
      toast.error({
        content: `Không thể tải file ${sop.code}. Vui lòng thử lại sau!`,
        key: `dl-${sop.code}`,
        duration: 3,
      });
    } finally {
      setDownloadingCode(null);
    }
  };

  return (
    <div>
      {sopAccess && !sopAccess.hasAccess && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 16,
            padding: '12px 16px',
            borderRadius: 12,
            background: '#fffbeb',
            border: '1px solid #fde68a',
          }}
        >
          <span style={{ color: '#92400e', fontSize: 14 }}>
            Đang dùng thử: tải miễn phí {(sopAccess.freeCodes || []).join(', ')}. Mua bộ đủ 26 quy trình để tải toàn bộ
            {buyPlan ? `, dùng ${buyPlan.durationMonths || 12} tháng` : ''}.
          </span>
          <Button type="primary" icon={<LockOutlined />} onClick={goToBuy} style={{ background: '#b45309', borderColor: '#b45309', fontWeight: 700 }}>
            {buyLabel}
          </Button>
        </div>
      )}
      {/* 1. Bảng Tóm Tắt Kết Quả Chẩn Đoán Của Cơ Sở (Matching 100% với 6 câu hỏi) */}
      <div
        style={{
          background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)',
          borderRadius: 14,
          padding: '20px 24px',
          marginBottom: 20,
          border: '1px solid #bbf7d0',
          boxShadow: '0 2px 6px rgba(0, 85, 55, 0.04)',
        }}
      >
        <Row gutter={[16, 14]} align="middle" justify="space-between">
          <Col xs={24} lg={17}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <Tag color="green" style={{ fontWeight: 800, padding: '2px 8px', borderRadius: 4 }}>
                KẾT QUẢ CHẨN ĐOÁN
              </Tag>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#005537' }}>
                {config.pharmacyName || '(Cơ sở chưa nhập tên)'}
              </span>
              <Tag color="blue">{config.pharmacyType === 'quay-thuoc' ? 'Quầy Thuốc' : 'Nhà Thuốc'}</Tag>
              <Tag color="purple">
                {config.staffCount === '1'
                  ? '1 Người (Chủ tự làm)'
                  : config.staffCount === '2'
                    ? '2 Người'
                    : `${config.staffCount} người`}
              </Tag>
              {config.shiftCount && <Tag color="orange">{config.shiftCount} ca/ngày</Tag>}
              {config.hasSoftware && (
                <Tag color="cyan">{config.hasSoftware === 'co' ? 'Đã có phần mềm' : 'Sổ tay thủ công'}</Tag>
              )}
            </div>

            <div style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.6 }}>
              {isSoloOrNoInternal ? (
                <span>
                  Hệ thống xác định cơ sở của bạn là <strong>quầy 1 người / chủ tự bán (không có hệ thống quản lý nội bộ)</strong>.
                  Hiển thị <strong>{totalActiveCount} quy trình thực chiến</strong> bạn thực sự cần dùng mỗi ngày.
                </span>
              ) : (
                <span>
                  Hệ thống xác định cơ sở của bạn có <strong>hệ thống nhân sự {config.staffCount} người</strong>.
                  Hiển thị đầy đủ <strong>{totalActiveCount} quy trình</strong> bao gồm trọn bộ quy trình Tuyển dụng, Đào tạo kèm cặp 30 ngày, Giao việc từ xa và Bàn giao ca.
                </span>
              )}
            </div>
          </Col>

          <Col xs={24} lg={7} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              ghost
              icon={<EditOutlined />}
              onClick={onNavigateToForm}
              style={{
                borderRadius: 8,
                borderColor: '#005537',
                color: '#005537',
                fontWeight: 600,
                height: 42,
              }}
            >
              Chẩn Đoán Lại / Đổi Câu Trả Lời
            </Button>
          </Col>
        </Row>
      </div>

      {/* Top Filter and Search Bar */}
      <div
        style={{
          background: '#ffffff',
          borderRadius: 12,
          padding: '18px 20px',
          marginBottom: 24,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
        }}
      >
        {/* Tìm kiếm quy trình */}
        <div style={{ marginBottom: 16 }}>
          <Input
            size="large"
            placeholder="Tìm kiếm mã (QT-01...), tên quy trình, người làm, từ khoá..."
            prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            allowClear
            style={{ borderRadius: 8 }}
          />
        </div>

        {/* 5 Dòng chảy Pill Filter */}
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            size="middle"
            type={selectedFlow === 'all' ? 'primary' : 'dashed'}
            onClick={() => setSelectedFlow('all')}
            style={selectedFlow === 'all' ? { background: '#005537', borderColor: '#005537', borderRadius: 20, fontWeight: 600 } : { borderRadius: 20 }}
          >
            Tất cả dòng chảy ({totalActiveCount})
          </Button>
          {PHARMACY_FLOWS.map((flow) => {
            const isSelected = selectedFlow === flow.id;
            const activeInFlow = flow.qtCodes.filter((c) => !isHiddenByConfig(c)).length;
            const isFlowEmpty = activeInFlow === 0;

            return (
              <Button
                key={flow.id}
                size="middle"
                icon={getFlowIcon(flow.id)}
                onClick={() => setSelectedFlow(flow.id)}
                style={{
                  borderRadius: 20,
                  borderColor: isSelected ? flow.color : isFlowEmpty ? '#e2e8f0' : '#cbd5e1',
                  background: isSelected ? flow.color : isFlowEmpty ? '#f8fafc' : '#ffffff',
                  color: isSelected ? '#ffffff' : isFlowEmpty ? '#94a3b8' : '#334155',
                  fontWeight: isSelected ? 700 : 500,
                }}
              >
                {flow.name} ({activeInFlow})
              </Button>
            );
          })}
        </div>

        {/* Hàng: Hiển thị tag các bộ lọc đang kích hoạt & Tổng kết quả */}
        {hasActiveFilters && (
          <div
            style={{
              marginTop: 14,
              paddingTop: 12,
              borderTop: '1px dashed #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                <FilterOutlined style={{ color: '#005537' }} /> Đang lọc:
              </span>

              {searchKeyword.trim() !== '' && (
                <Tag
                  closable
                  onClose={() => setSearchKeyword('')}
                  color="blue"
                  style={{ borderRadius: 12, fontSize: 12 }}
                >
                  Từ khoá: &quot;{searchKeyword}&quot;
                </Tag>
              )}

              {selectedFlow !== 'all' && (
                <Tag
                  closable
                  onClose={() => setSelectedFlow('all')}
                  color="green"
                  style={{ borderRadius: 12, fontSize: 12 }}
                >
                  Dòng: {PHARMACY_FLOWS.find((f) => f.id === selectedFlow)?.name || selectedFlow}
                </Tag>
              )}

              <Button
                type="link"
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={handleResetFilters}
                style={{ color: '#ef4444', fontSize: 12, padding: '0 4px', fontWeight: 600 }}
              >
                Xoá tất cả bộ lọc
              </Button>
            </div>

            <div style={{ fontSize: 13, fontWeight: 700, color: '#005537' }}>
              Hiển thị: {filteredSops.length} / {totalActiveCount} quy trình phù hợp
            </div>
          </div>
        )}


      </div>

      {/* Grid SOP Cards */}
      {filteredSops.length === 0 ? (
        <div style={{ background: '#ffffff', borderRadius: 12, padding: '48px 24px', textAlign: 'center', border: '1px dashed #cbd5e1' }}>
          {selectedFlow === 'flow-nguoi' && isSoloOrNoInternal ? (
            <div>
              <TeamOutlined style={{ fontSize: 48, color: '#a78bfa', marginBottom: 16 }} />
              <h4 style={{ fontSize: 17, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
                Dòng Người (Quản Lý Hệ Thống Nội Bộ) Không Áp Dụng Cho Quầy Của Bạn
              </h4>
              <p style={{ fontSize: 13.5, color: '#64748b', maxWidth: 620, margin: '0 auto 20px auto', lineHeight: 1.6 }}>
                Với mô hình <strong>Quầy không có hệ thống nội bộ (chủ tự đứng bán hoặc 1 người)</strong>, các quy trình Tuyển dụng (QT-20), Kèm nhân viên 30 ngày (QT-21), Giao việc từ xa (QT-22) và Xử lý nhân viên sai phạm/nghỉ việc (QT-23) được hệ thống tự động ẩn để tránh gây rối mắt và rườm rà tài liệu.
              </p>
              <Space>
                <Button
                  type="primary"
                  onClick={onNavigateToForm}
                  style={{ background: '#005537', borderColor: '#005537', borderRadius: 6 }}
                >
                  Chẩn Đoán Lại Để Kích Hoạt Dòng Người
                </Button>
                <Button onClick={() => setSelectedFlow('all')} style={{ borderRadius: 6 }}>
                  Xem các dòng quy trình khác
                </Button>
              </Space>
            </div>
          ) : (
            <div>
              <Empty description="Không tìm thấy quy trình phù hợp với bộ lọc hiện tại" />
              <div style={{ marginTop: 8, fontSize: 13, color: '#64748b' }}>
                Thử thay đổi đợt triển khai, dòng chảy, trạng thái hoặc từ khoá tìm kiếm.
              </div>
              <Button
                type="primary"
                style={{ marginTop: 16, background: '#005537', borderColor: '#005537', borderRadius: 6, fontWeight: 600 }}
                onClick={handleResetFilters}
              >
                Xoá bộ lọc để xem lại toàn bộ
              </Button>
            </div>
          )}
        </div>
      ) : (
        <Row gutter={[20, 20]}>
          {filteredSops.map((sop) => {
            const isDone = completedSopCodes.includes(sop.code);
            const isHidden = isHiddenByConfig(sop.code);
            const flowObj = getFlowObj(sop);
            const fileSize = sop.fileSizeKb || 20;

            return (
              <Col xs={24} sm={12} lg={8} key={sop.code}>
                <div
                  className="process-card"
                  style={{
                    borderColor: isDone ? '#86efac' : isHidden ? '#cbd5e1' : '#e2e8f0',
                    background: isDone ? '#f0fdf4' : isHidden ? '#f8fafc' : '#ffffff',
                    opacity: isHidden ? 0.75 : 1,
                  }}
                >
                  {/* Card Header: Code & Badges */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <span
                        className="process-card-code"
                        style={{
                          background: isDone ? '#dcfce7' : `${flowObj.color}15`,
                          color: isDone ? '#16a34a' : flowObj.color,
                        }}
                      >
                        {sop.code}
                      </span>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {sop.phase === 1 && (
                          <Tag color="green" style={{ margin: 0, fontSize: 11, fontWeight: 600 }}>
                            Đợt 1 Cốt Lõi
                          </Tag>
                        )}
                        {sop.phase === 2 && (
                          <Tag color="blue" style={{ margin: 0, fontSize: 11, fontWeight: 600 }}>
                            Đợt 2 Tiền & Hàng
                          </Tag>
                        )}
                        {sop.phase === 3 && (
                          <Tag color="purple" style={{ margin: 0, fontSize: 11, fontWeight: 600 }}>
                            Đợt 3 Mở Rộng
                          </Tag>
                        )}
                        {isHidden && (
                          <Tag color="warning" style={{ margin: 0, fontSize: 11 }}>
                            Ẩn: Quản lý nội bộ
                          </Tag>
                        )}
                      </div>
                    </div>

                    <h4 className="process-card-title">{sop.title}</h4>

                    <div
                      style={{
                        fontSize: 12,
                        color: '#64748b',
                        marginBottom: 10,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <span style={{ flexShrink: 0 }}>👤</span>
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={sop.executor || sop.targetRole || 'Dược sĩ / Nhân viên'}
                        >
                          {sop.executor || sop.targetRole || 'Dược sĩ / Nhân viên'}
                        </span>
                      </div>

                      <span
                        style={{
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          color: '#0284c7',
                          background: '#f0f9ff',
                          border: '1px solid #bae6fd',
                          padding: '2px 8px',
                          borderRadius: 6,
                          fontWeight: 600,
                          fontSize: 11.5,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        {fileSize} KB (.docx)
                      </span>
                    </div>

                    <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.5, margin: '0 0 10px 0' }}>
                      {sop.purpose || sop.summary}
                    </p>

                    {/* Chi tiết đắt giá nhất */}
                    {(sop.keyAdvice || sop.keyDetail) && (
                      <div className="process-key-detail">
                        <div className="process-key-detail-label">
                          <StarOutlined /> Điểm then chốt thực chiến
                        </div>
                        {sop.keyAdvice || sop.keyDetail}
                      </div>
                    )}
                  </div>

                  {/* Card Footer Actions: Direct Word Download & Preview Modal */}
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <Checkbox
                        checked={isDone}
                        onChange={() => onToggleCompleteSop(sop.code)}
                        style={{ fontSize: 13, fontWeight: 600, color: isDone ? '#16a34a' : '#475569' }}
                      >
                        {isDone ? 'Đã áp dụng tại quầy' : 'Đánh dấu đã áp dụng'}
                      </Checkbox>

                      <Tag style={{ margin: 0, fontSize: 11 }}>{flowObj.name}</Tag>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <Button
                        size="middle"
                        icon={<InfoCircleOutlined />}
                        onClick={() => setPreviewSop(sop)}
                        style={{ borderRadius: 8, fontSize: 12.5, fontWeight: 600, color: '#0f172a', borderColor: '#cbd5e1' }}
                      >
                        Xem Tóm Tắt
                      </Button>
                      <Button
                        size="middle"
                        type="primary"
                        icon={<FileWordOutlined />}
                        loading={downloadingCode === sop.code}
                        onClick={(e) => handleDownload(e, sop)}
                        style={{
                          borderRadius: 8,
                          fontSize: 12.5,
                          fontWeight: 700,
                          background: isLocked(sop) ? '#b45309' : '#005537',
                          borderColor: isLocked(sop) ? '#b45309' : '#005537',
                        }}
                      >
                        {isLocked(sop) ? (
                          <>
                            <LockOutlined /> Mua để tải
                          </>
                        ) : isTrial(sop) ? (
                          'Tải File Word (dùng thử)'
                        ) : (
                          'Tải File Word'
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      )}

      {/* Quick Summary Modal */}
      <Modal
        open={!!previewSop}
        onCancel={() => setPreviewSop(null)}
        width={680}
        title={
          previewSop && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag color="green" style={{ fontSize: 14, fontWeight: 800, padding: '2px 8px' }}>
                {previewSop.code}
              </Tag>
              <span style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                {previewSop.title}
              </span>
            </div>
          )
        }
        footer={
          previewSop && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Checkbox
                checked={completedSopCodes.includes(previewSop.code)}
                onChange={() => onToggleCompleteSop(previewSop.code)}
                style={{ fontWeight: 600 }}
              >
                {completedSopCodes.includes(previewSop.code) ? 'Đã áp dụng tại quầy' : 'Đánh dấu đã áp dụng'}
              </Checkbox>
              <Space>
                <Button onClick={() => setPreviewSop(null)}>Đóng</Button>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  size="large"
                  loading={downloadingCode === previewSop?.code}
                  onClick={(e) => handleDownload(e, previewSop)}
                  style={{ background: '#005537', borderColor: '#005537', fontWeight: 700 }}
                >
                  {isLocked(previewSop) ? buyLabel : 'Tải Bản Word (.docx) Đầy Đủ'}
                </Button>
              </Space>
            </div>
          )
        }
      >
        {previewSop && (
          <div style={{ padding: '8px 0' }}>
            <Descriptions bordered size="middle" column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Mục đích">
                {previewSop.purpose || previewSop.summary}
              </Descriptions.Item>
              <Descriptions.Item label="Phạm vi áp dụng">
                {previewSop.scope || 'Toàn bộ cơ sở và các giao dịch liên quan.'}
              </Descriptions.Item>
              <Descriptions.Item label="Người thực hiện">
                <strong>{previewSop.executor || previewSop.targetRole || 'Dược sĩ phụ trách'}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Người kiểm tra / Phê duyệt">
                {previewSop.reviewer || 'Chủ nhà thuốc / Quản lý chuyên môn'}
              </Descriptions.Item>
              <Descriptions.Item label="Tần suất thực hiện">
                {previewSop.frequency || 'Mỗi khi phát sinh công việc thực tế'}
              </Descriptions.Item>
              <Descriptions.Item label="Tên tài liệu đính kèm">
                <span style={{ color: '#0284c7', fontWeight: 600 }}>
                  <FileWordOutlined /> {previewSop.fileName || `${previewSop.code}.docx`}
                </span>{' '}
                <Tag color="cyan">~{previewSop.fileSizeKb || 20} KB</Tag>
              </Descriptions.Item>
            </Descriptions>

            {(previewSop.keyAdvice || previewSop.keyDetail) && (
              <div
                style={{
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: 8,
                  padding: '12px 16px',
                  marginTop: 12,
                }}
              >
                <div style={{ fontWeight: 700, color: '#16a34a', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <StarOutlined /> Chi tiết đắt giá & Kinh nghiệm thực chiến:
                </div>
                <div style={{ color: '#1e293b', fontSize: 13.5, lineHeight: 1.5 }}>
                  {previewSop.keyAdvice || previewSop.keyDetail}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
